import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Meteor } from 'meteor/meteor';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { css } from '/imports/ui/css';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { useTracker } from '/imports/hooks';
import {
  BannerPosition, clampBannerPosition, endImpersonation, isImpersonating, readBannerPosition,
  readImpersonation, writeBannerPosition,
} from '/imports/impersonation';

const ImpersonationBannerStyled = styled.div`
  position: fixed;
  /* Bottom right by default: the authenticated app already parks its toasts top-centre, and a banner that
     covered them would hide the very errors an impersonated session is usually being used to reproduce.
     Dragging replaces this with an inline left/top (see the grip). */
  right: 1rem;
  bottom: 1rem;
  z-index: 9000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: calc(100vw - 2rem);
  padding: 0.5rem 0.75rem 0.5rem 0.5rem;
  border-radius: ${css.borderRadius};
  border: 1px solid ${css.quaternaryColor};
  background: ${css.navBackground};
  color: ${css.navText};
  box-shadow: 0 0.25rem 1rem rgba(0, 0, 0, 0.35);
  /* The pill is chrome, not content: a drag across it should move it, not select its text or scroll the
     page under it. */
  user-select: none;
  touch-action: none;
  .grip {
    display: flex;
    align-items: center;
    align-self: stretch;
    padding: 0 0.25rem;
    cursor: grab;
    color: ${css.grey4};
    &:active {
      cursor: grabbing;
    }
    /* Three dots in a column, the usual handle, drawn rather than iconised. */
    .dots {
      display: grid;
      grid-template-columns: repeat(2, 0.1875rem);
      gap: 0.1875rem;
      span {
        width: 0.1875rem;
        height: 0.1875rem;
        border-radius: 50%;
        background: currentColor;
      }
    }
  }
  &.dragging {
    .grip { cursor: grabbing; }
    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.45);
  }
  .who {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }
  .icon svg {
    fill: ${css.quaternaryColor};
  }
  .label {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    min-width: 0;
    .caption {
      font-size: 0.75rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: ${css.grey5};
    }
    .name {
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  ${css.mobileMedia} {
    /* Clear of the mobile footer nav, which owns the bottom of the screen. */
    bottom: calc(${css.navHeightMobile} + 1rem);
  }
`;

/**
 * The way back out of an impersonated session. Rendered on every authenticated page, and visible only to a
 * browser that is actually in one — the stash is written by the Impersonate buttons on the user and account
 * pages, and cleared on the way out or on logout. Draggable by its grip, since a fixed pill will sometimes
 * sit on top of something the super user needs to click.
 */
export const ImpersonationBanner = () => {
  const userId = useTracker(() => Meteor.userId(), []);
  const [leaving, setLeaving] = useState(false);
  const [position, setPosition] = useState<BannerPosition | undefined>(() => readBannerPosition());
  const [dragging, setDragging] = useState(false);
  const banner = useRef<HTMLDivElement>(null);
  const impersonation = readImpersonation();
  const impersonating = isImpersonating(impersonation, userId);
  /** Brings a stored position back inside the window, on mount and whenever the window changes size. */
  useEffect(() => {
    if (!impersonating) return;
    const contain = () => setPosition((current) => {
      const element = banner.current;
      if (!current || !element) return current;
      const { width, height } = element.getBoundingClientRect();
      return clampBannerPosition(current, { width, height }, { width: window.innerWidth, height: window.innerHeight });
    });
    contain();
    window.addEventListener('resize', contain);
    return () => window.removeEventListener('resize', contain);
  }, [impersonating]);
  if (!impersonating) return null;
  /**
   * Moves the pill with the pointer. The grip is the only handle, so a press on the Exit button is still a
   * click and never the start of a drag.
   * @param event - the pointer-down that begins the drag
   */
  const startDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const element = banner.current;
    if (!element) return;
    event.preventDefault();
    const rect = element.getBoundingClientRect();
    const grabX = event.clientX - rect.left;
    const grabY = event.clientY - rect.top;
    const size = { width: rect.width, height: rect.height };
    let latest: BannerPosition = { left: rect.left, top: rect.top };
    setDragging(true);
    const move = (ev: PointerEvent) => {
      latest = clampBannerPosition(
        { left: ev.clientX - grabX, top: ev.clientY - grabY },
        size,
        { width: window.innerWidth, height: window.innerHeight },
      );
      setPosition(latest);
    };
    const up = () => {
      setDragging(false);
      writeBannerPosition(latest);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const exit = async () => {
    setLeaving(true);
    try {
      const returnPath = await endImpersonation();
      // A full load rather than a route change: everything on screen was fetched as somebody else.
      window.location.href = returnPath || '/';
    }
    catch {
      setLeaving(false);
      toast.error(strings.ImpersonationExitFailed());
      window.location.href = '/login';
    }
  };
  return (
    <ImpersonationBannerStyled
      ref={banner}
      className={`impersonation-banner${dragging ? ' dragging' : ''}`}
      // Once dragged the pill is placed from the top left, so the default corner offsets have to go.
      style={position ? { left: position.left, top: position.top, right: 'auto', bottom: 'auto' } : undefined}
    >
      <div className="grip" data-tooltip-id="impersonation-grip" onPointerDown={startDrag}>
        <span className="dots">
          <span /><span /><span /><span /><span /><span />
        </span>
      </div>
      <Tooltip id="impersonation-grip" content={strings.DragToMove()} place="top" openEvents={{ mouseover: true }} />
      <div className="who">
        <Icon icon="visibility" />
        <div className="label">
          <span className="caption">{strings.ImpersonationViewingAs()}</span>
          <span className="name">{impersonation?.label}</span>
        </div>
      </div>
      <Button className="btn primary exit-impersonation" disabled={leaving} onClick={() => exit()}>
        {strings.ImpersonationExit()}
      </Button>
    </ImpersonationBannerStyled>
  );
};
